import React from 'react';
import { Checkbox } from '@mui/material';
import {
  DocumentFormat,
  PacketTypeOption,
} from '../../../@types/FlightBriefing.type';
import { getPacketDisplayName } from './packetTypes';

interface PacketTypeTableProps {
  packetTypes: PacketTypeOption[];
  selected: string[];
  disabled?: boolean;
  onToggle: (value: string) => void;
  onToggleAll: (checked: boolean) => void;
}

const FileIcon: React.FC<{ format: DocumentFormat }> = ({ format }) => (
  <svg
    className={`dp-file-icon dp-file-icon--${format.toLowerCase()}`}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <rect x="3" y="2" width="18" height="20" rx="2" fill="currentColor" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontSize="7"
      fontWeight="700"
      fill="#fff"
    >
      {format}
    </text>
  </svg>
);

const PacketTypeTable: React.FC<PacketTypeTableProps> = ({
  packetTypes,
  selected,
  disabled = false,
  onToggle,
  onToggleAll,
}) => {
  const allSelected =
    packetTypes.length > 0 &&
    packetTypes.every((p) => selected.includes(p.value));
  const someSelected =
    !allSelected && packetTypes.some((p) => selected.includes(p.value));

  return (
    <section
      className="dp-card dp-card--packets"
      aria-labelledby="dp-packet-heading"
    >
      <h2 id="dp-packet-heading" className="dp-card__title">
        2. Select a packet type
      </h2>
      <p className="dp-card__subtitle">
        Select one or more documents. Multiple documents are downloaded as a
        single ZIP file.
      </p>

      <div className="dp-table-wrapper">
        <table className="dp-table" data-testid="packet-type-table">
          <thead>
            <tr>
              <th className="dp-table__check">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={disabled || packetTypes.length === 0}
                  onChange={(event) => onToggleAll(event.target.checked)}
                  inputProps={{ 'aria-label': 'Select all packet types' }}
                />
              </th>
              <th>Packet type</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {packetTypes.map((packet) => {
              const isSelected = selected.includes(packet.value);
              return (
                <tr
                  key={packet.value}
                  className={isSelected ? 'is-selected' : undefined}
                >
                  <td className="dp-table__check">
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => onToggle(packet.value)}
                      inputProps={{
                        'aria-label': getPacketDisplayName(packet),
                      }}
                    />
                  </td>
                  <td>{packet.title}</td>
                  <td>
                    <span className="dp-file">
                      <FileIcon format={packet.format} />
                      {packet.format}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PacketTypeTable;
